// packages/shared/src/team/limit-request.types.ts
//
// LimitRequest domain TS interfaces + Zod schemas (P3.4a.).
//
// Node-pure (no Fastify / Mongoose). Safe to bundle in Vite client builds.
//
// Design invariants (P3.4a.):
//   - 1 pending request per member at any time (DB-enforced via partial
//     unique index on { memberId } where status = "pending").
//   - reason: required, min 5 chars (cap 500 to deter abuse).
//   - durationDays: integer 1..365.
//   - requestedAmountCents: positive int32, capped at $1M (100_000_000).
//   - reviewNote: required when status = "rejected".
//   - Append-only history: settled requests (status != pending) are
//     immutable; "cancelled" is a status flip, never a delete.
//
// Cross-ref: API-CONTRACTS.md POST/GET /limit-requests, Wave 2 Task 6
//   (LimitRequestService), Wave 5 Task 13 (handler).
import { z } from "zod";
// ---------------------------------------------------------------------------
// Local primitives
//
// Inline copies of ObjectIdHex + UnixMs to keep this module standalone and
// avoid hard ordering dependencies during Wave 1 parallel builds.
// Reviewer deduplicates post-wave (same convention as wallet/types.ts).
// ---------------------------------------------------------------------------
/** MongoDB ObjectId hex string (24 lowercase hex chars). */
const ObjectIdHexSchema = z.string().regex(/^[a-f0-9]{24}$/, {
    message: "Must be a 24-character lowercase hex ObjectId",
});
/** Unix milliseconds timestamp (non-negative integer). */
const UnixMsSchema = z.number().int().nonnegative();
// ---------------------------------------------------------------------------
// LimitRequestStatus — lifecycle states (P3.4a.)
// ---------------------------------------------------------------------------
/**
 * Lifecycle status for a member's spending-limit increase request.
 *
 * - pending   — submitted, awaiting Team Lead / Owner review
 * - approved  — Team Lead approved; budget delta applied to member
 * - rejected  — Team Lead rejected; reviewNote required
 * - cancelled — member self-cancelled while still pending
 *
 * Once a request leaves "pending" it is immutable history (append-only).
 */
export const LimitRequestStatusSchema = z.enum([
    "pending",
    "approved",
    "rejected",
    "cancelled",
]);
// ---------------------------------------------------------------------------
// Submission schema — POST /limit-requests body
//
// Mirrors API-CONTRACTS.md PostLimitRequestsRequest. Re-exported here so
// FE forms can import the same Zod object the BE handler validates with.
// ---------------------------------------------------------------------------
export const createLimitRequestSchema = z
    .object({
    /** Cap of $1M (100_000_000 cents) deters abuse / overflow. */
    requestedAmountCents: z.number().int().positive().max(100_000_000),
    reason: z.string().min(5).max(500),
    durationDays: z.number().int().min(1).max(365),
})
    .strict();
// ---------------------------------------------------------------------------
// Review schema — POST /limit-requests/:id/approve|reject body
//
// `decision="rejected"` requires a non-empty reviewNote (Mongoose validator
// enforces same rule on the persisted doc).
// ---------------------------------------------------------------------------
export const reviewLimitRequestSchema = z
    .object({
    decision: z.enum(["approved", "rejected"]),
    reviewNote: z.string().min(1).max(500).optional(),
})
    .strict()
    .refine((data) => data.decision !== "rejected" ||
    (typeof data.reviewNote === "string" && data.reviewNote.trim().length > 0), {
    message: "reviewNote is required when decision is 'rejected'",
    path: ["reviewNote"],
});
// ---------------------------------------------------------------------------
// Row schema — list-view shape returned from GET /limit-requests
//
// Adds enrichment fields (memberDisplayName, teamName, reviewedByDisplayName)
// joined by the service layer; bare model has only ids.
// ---------------------------------------------------------------------------
export const limitRequestRowSchema = z.object({
    requestId: ObjectIdHexSchema,
    memberId: ObjectIdHexSchema,
    memberDisplayName: z.string(),
    teamId: ObjectIdHexSchema,
    teamName: z.string(),
    requestedAmountCents: z.number().int().positive(),
    reason: z.string(),
    durationDays: z.number().int().positive(),
    status: LimitRequestStatusSchema,
    reviewedByUserId: ObjectIdHexSchema.nullable(),
    reviewedByDisplayName: z.string().nullable(),
    reviewedAt: UnixMsSchema.nullable(),
    reviewNote: z.string().nullable(),
    submittedAt: UnixMsSchema,
});
//# sourceMappingURL=limit-request.types.js.map